/**
 * PREFERENCES STORE - Gerenciamento de preferências do usuário
 * 
 * v3.1 - Adicionado controle de visibilidade de seções da sidebar
 * 
 * v3.0 SIMPLIFICAÇÃO DIA 1:
 * - customUsageTags mantido para retrocompatibilidade (dados existentes)
 * - Actions de usage tags marcadas como deprecated
 * 
 * Armazena preferências em localStorage (para acesso rápido)
 * Inclui sistema de tags personalizadas para anotações.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { googleDrive } from '../services/googleDrive';
import { logger } from '../utils/logger';
import type { EmpiricalTagDefinition } from '../constants/analysis-techniques';

// Tipos para escala de fonte
export type FontScale = 'small' | 'medium' | 'large' | 'xlarge';
export type FontWeight = 'normal' | 'medium' | 'semibold';

// Tipo para modo de visualização de PDF
export type PdfViewMode = 'page' | 'scroll';

// Tipo para precisão do quiz de arquétipo
export type QuizPrecisionLevel = 'quick' | 'moderate' | 'detailed';

// Tipo para intensidade do overlay
export type OverlayIntensity = 'none' | 'subtle' | 'medium' | 'intense';

export const OVERLAY_INTENSITY_LABELS: Record<OverlayIntensity, string> = {
  none: 'Nenhum',
  subtle: 'Suave',
  medium: 'Médio',
  intense: 'Intenso',
};

export const OVERLAY_INTENSITY_VALUES: Record<OverlayIntensity, number> = {
  none: 0,
  subtle: 0.3,
  medium: 0.6,
  intense: 0.95,
};

// Tipo para cor do overlay
export type OverlayColor = 'green' | 'dark';

export const OVERLAY_COLOR_LABELS: Record<OverlayColor, string> = {
  green: 'Verde Bigwa',
  dark: 'Escuro',
};

export const OVERLAY_COLOR_VALUES: Record<OverlayColor, string> = {
  green: '71, 123, 114',   // #477B72
  dark: '0, 0, 0',
};

// Tipo para idioma de tradução
export type TranslationLanguage = 'pt-BR' | 'pt-PT' | 'en-US' | 'es' | 'it' | 'fr' | 'de';

export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  small: 'Pequeno',
  medium: 'Médio',
  large: 'Grande',
  xlarge: 'Extra Grande',
};

export const FONT_SCALE_VALUES: Record<FontScale, number> = {
  small: 0.9,
  medium: 1,
  large: 1.15,
  xlarge: 1.3,
};

export const FONT_WEIGHT_LABELS: Record<FontWeight, string> = {
  normal: 'Leve',
  medium: 'Normal',
  semibold: 'Forte',
};

export const FONT_WEIGHT_VALUES: Record<FontWeight, number> = {
  normal: 400,
  medium: 500,
  semibold: 600,
};

// Idiomas disponíveis para tradução
export const TRANSLATION_LANGUAGES: Record<TranslationLanguage, { label: string; flag: string }> = {
  'pt-BR': { label: 'Português (Brasil)', flag: '🇧🇷' },
  'pt-PT': { label: 'Português (Portugal)', flag: '🇵🇹' },
  'en-US': { label: 'English (US)', flag: '🇺🇸' },
  'es': { label: 'Español', flag: '🇪🇸' },
  'it': { label: 'Italiano', flag: '🇮🇹' },
  'fr': { label: 'Français', flag: '🇫🇷' },
  'de': { label: 'Deutsch', flag: '🇩🇪' },
};

// Tipos para preferências de nomeação de PDFs
export type NamingSeparator = '_' | '-' | '.';
export type NamingAuthorFormat = 'lastName' | 'lastNameInitial' | 'fullName';

export interface NamingPreferences {
  /** Separador entre partes do nome (padrão: _) */
  separator: NamingSeparator;
  /** Formato do nome do autor */
  authorFormat: NamingAuthorFormat;
  /** Incluir título no nome do arquivo */
  includeTitle: boolean;
  /** Máximo de palavras do título */
  maxTitleWords: number;
  /** [INCREMENTAL] Adicionar prefixo com nome do projeto */
  includeProjectPrefix: boolean;
  /** [INCREMENTAL] Adicionar tipo de trabalho ao final */
  includeWorkType: boolean;
}

export const DEFAULT_NAMING_PREFERENCES: NamingPreferences = {
  separator: '_',
  authorFormat: 'lastName',
  includeTitle: true,
  maxTitleWords: 4,
  includeProjectPrefix: false,
  includeWorkType: false,
};

export const NAMING_SEPARATOR_LABELS: Record<NamingSeparator, string> = {
  '_': 'Underline (Santos_2020)',
  '-': 'Hífen (Santos-2020)',
  '.': 'Ponto (Santos.2020)',
};

export const NAMING_AUTHOR_FORMAT_LABELS: Record<NamingAuthorFormat, string> = {
  'lastName': 'Sobrenome (Santos)',
  'lastNameInitial': 'Sobrenome + Inicial (Santos-J)',
  'fullName': 'Nome Completo (Santos-Joao)',
};

// ===== STATUS DE LEITURA CUSTOMIZÁVEIS =====

export interface ReadingStatus {
  id: string;
  name: string;
  color: string;
  order: number;
  isDefault: boolean;
}

export const DEFAULT_READING_STATUSES: ReadingStatus[] = [
  { id: 'not-read', name: 'Não Lido', color: '#C4C4C6', order: 0, isDefault: true },
  { id: 'next', name: 'Próximo', color: '#9B59B6', order: 1, isDefault: true },
  { id: 'reading', name: 'Lendo', color: '#F2BC41', order: 2, isDefault: true },
  { id: 'read', name: 'Lido', color: '#477B72', order: 3, isDefault: true },
];

export const CUSTOM_STATUS_PALETTE: { color: string; name: string }[] = [
  { color: '#60A5FA', name: 'Azul' },
  { color: '#D4918A', name: 'Coral' },
  { color: '#FB923C', name: 'Laranja' },
  { color: '#F472B6', name: 'Rosa' },
];

export const QUICK_STATUS_SUGGESTIONS: { name: string; color: string }[] = [
  { name: 'Discutir com orientador', color: '#60A5FA' },
  { name: 'Fichar', color: '#D4918A' },
  { name: 'Reler', color: '#FB923C' },
  { name: 'Descartado', color: '#F472B6' },
];

export const MAX_READING_STATUSES = 8;

export interface BigwaPreferences {
  /** Controla a visibilidade dos títulos de seção na barra lateral */
  showSidebarSectionTitles: boolean;
  
  /** Iniciar com sidebar recolhida */
  startWithSidebarCollapsed: boolean;
  
  /** Classificação aprofundada (apenas para plano Básico) */
  useDetailedClassification: boolean;
  
  /** Escala de fonte para conteúdo */
  fontScale: FontScale;
  
  /** Intensidade de fonte para conteúdo */
  fontWeight: FontWeight;
  
  /** Intensidade do overlay/backdrop dos modais */
  overlayIntensity: OverlayIntensity;

  /** Cor do overlay/backdrop dos modais */
  overlayColor: OverlayColor;

  /** Nível de precisão do quiz de arquétipo */
  quizPrecisionLevel: QuizPrecisionLevel;
  
  /** Idioma alvo para traduções */
  translationLanguage: TranslationLanguage;
  
  // ===== VISIBILIDADE DE SEÇÕES DA SIDEBAR (v3.1) =====
  
  /** Exibir Repositório e Dados da Pesquisa (para pesquisa empírica) */
  showEmpiricalResearch: boolean;
  
  /** Exibir página Conexões */
  showConexoes: boolean;
  
  /** Exibir página Colaboração */
  showColaboracao: boolean;
  showCitacoes: boolean;

  // ===== SISTEMA DUAL DE TAGS =====
  
  /** Modo de navegação de PDF: página ou rolagem contínua */
  pdfViewMode: PdfViewMode;

  /** Exibir toast ao retomar leitura de PDF */
  showResumeToast: boolean;

  /** Preferências de nomeação de PDFs */
  namingPreferences: NamingPreferences;
  
  // ===== GOOGLE DRIVE =====

  /** Devolver PDF à pasta original ao excluir referência importada via Picker */
  returnPdfOnDelete: boolean;

  /** Tags rápidas personalizadas de CONTEÚDO (Eixo 1) */
  customContentTags: string[];
  
  /** Mostrar texto da relevância (Central/Importante/Dúvida) na coluna Leituras */
  showRelevanceLabel: boolean;

  /** @deprecated v3.0: Mantido apenas para retrocompatibilidade com dados existentes */
  customUsageTags: string[];

  /** Nome acadêmico do usuário (como aparece nas publicações) — usado para detecção automática de autoria */
  academicName: string;

  /** Nomes alternativos usados em publicações (variações, abreviações) */
  alternativeNames: string[];

  /** Incluir orientações (teses/dissertações orientadas) na detecção de autoria */
  detectOrientations: boolean;

  /** ID da instituição CAFe selecionada para acesso ao Portal CAPES */
  cafeInstitutionId: string | null;

  /** Data ISO da última exportação para o Lattes */
  lattesLastExportDate: string | null;
  /** Quantidade de obras confirmadas no momento da exportação */
  lattesExportObrasCount: number;

  // ===== STATUS DE LEITURA CUSTOMIZÁVEIS =====

  /** Lista de status de leitura (defaults + customizados) */
  readingStatuses: ReadingStatus[];

  // ===== TAGS EMPÍRICAS (Fase Empírica) =====

  /** IDs das técnicas de análise ativas (ex: 'direito', 'bardin') */
  activeTechniques?: string[];

  /** Tags empíricas customizadas do pesquisador */
  customEmpiricalTags?: EmpiricalTagDefinition[];

  /** Onboarding contextual de tags empíricas já concluído */
  empiricalOnboardingCompleted?: boolean;
}

interface PreferencesState {
  preferences: BigwaPreferences;
  isLoading: boolean;
  
  // Actions
  updatePreference: <K extends keyof BigwaPreferences>(
    key: K, 
    value: BigwaPreferences[K]
  ) => void;
  loadPreferences: () => Promise<void>;
  resetPreferences: () => void;
  applyTemplate: (template: 'pesquisa' | 'leitura' | 'personalizado') => void;

  // ===== ACTIONS PARA TAGS =====
  addCustomContentTag: (tag: string) => void;
  removeCustomContentTag: (tag: string) => void;
  resetCustomContentTags: () => void;
  
  // ===== ACTIONS PARA NOMEAÇÃO =====
  updateNamingPreference: <K extends keyof NamingPreferences>(
    key: K,
    value: NamingPreferences[K]
  ) => void;
  resetNamingPreferences: () => void;
  
  /** @deprecated v3.0: Mantido para retrocompatibilidade */
  addCustomUsageTag: (tag: string) => void;
  /** @deprecated v3.0: Mantido para retrocompatibilidade */
  removeCustomUsageTag: (tag: string) => void;
  /** @deprecated v3.0: Mantido para retrocompatibilidade */
  resetCustomUsageTags: () => void;

  // ===== ACTIONS PARA MINHAS OBRAS =====
  addAlternativeName: (name: string) => void;
  removeAlternativeName: (name: string) => void;

  // ===== ACTIONS PARA STATUS DE LEITURA =====
  addReadingStatus: (name: string, color: string) => void;
  updateReadingStatus: (id: string, updates: Partial<Pick<ReadingStatus, 'name' | 'color'>>) => void;
  removeReadingStatus: (id: string) => void;

  // ===== ACTIONS PARA TAGS EMPÍRICAS =====
  setActiveTechniques: (techniqueIds: string[]) => void;
  addCustomEmpiricalTag: (tag: EmpiricalTagDefinition) => void;
  removeCustomEmpiricalTag: (tagId: string) => void;
  setEmpiricalOnboardingCompleted: (completed: boolean) => void;

  // ===== DRIVE SYNC =====
  mergeFromDrive: (drivePrefs: Partial<BigwaPreferences>) => void;
  revalidatePreferencesIfStale: () => void;
}

// Valores padrão
const DEFAULT_PREFERENCES: BigwaPreferences = {
  showSidebarSectionTitles: true,
  startWithSidebarCollapsed: false,
  useDetailedClassification: false, // Sumária por padrão
  fontScale: 'medium',
  fontWeight: 'medium',
  overlayIntensity: 'medium',
  overlayColor: 'green',
  quizPrecisionLevel: 'moderate', // Precisão moderada por padrão
  translationLanguage: 'pt-BR', // Português Brasil por padrão
  // v3.1: Todas as seções visíveis por padrão
  showEmpiricalResearch: true,
  showConexoes: true,
  showColaboracao: true,
  showCitacoes: true,
  pdfViewMode: 'scroll',
  showResumeToast: true,
  namingPreferences: { ...DEFAULT_NAMING_PREFERENCES },
  returnPdfOnDelete: true,
  showRelevanceLabel: true,
  customContentTags: [],
  customUsageTags: [],
  academicName: '',
  alternativeNames: [],
  detectOrientations: false,
  cafeInstitutionId: null,
  lattesLastExportDate: null,
  lattesExportObrasCount: 0,
  readingStatuses: [...DEFAULT_READING_STATUSES],
};

/**
 * Normaliza uma lista de tags custom vinda de fora do store. Dado legado no
 * Drive/localStorage de alguns usuários guarda entradas como { tag: 'x' } em
 * vez do tipo declarado string[] — o hotfix #3 (2fb14551) blindou só o render
 * do PreferenciasModal e o shape corrompido seguiu entrando pelas fronteiras,
 * até derrubar o BigwaSearchFilters (Sprint 031, React error #31). Mantém
 * strings, extrai `.tag` de objetos legados, descarta o resto; dedupe preserva
 * a primeira ocorrência (mesmo invariante de addCustomContentTag). O próximo
 * save persiste o shape são — a correção cura o dado adiante, não só o render.
 */
export function normalizeCustomTagList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const tags: string[] = [];
  for (const entry of raw) {
    const value = typeof entry === 'string'
      ? entry
      : entry && typeof entry === 'object' && typeof (entry as { tag?: unknown }).tag === 'string'
        ? (entry as { tag: string }).tag
        : null;
    const trimmed = value?.trim();
    if (trimmed && !tags.includes(trimmed)) tags.push(trimmed);
  }
  return tags;
}

/**
 * Funde preferences vindas de fora (Drive/localStorage) sobre os defaults e
 * sela os campos string[] de tags contra o shape legado — toda entrada
 * wholesale do store passa por aqui (mergeFromDrive, revalidação, rehydrate).
 */
function mergeExternalPreferences(prefs: Partial<BigwaPreferences> | undefined): BigwaPreferences {
  const merged = { ...DEFAULT_PREFERENCES, ...(prefs || {}) };
  merged.customContentTags = normalizeCustomTagList(merged.customContentTags);
  merged.customUsageTags = normalizeCustomTagList(merged.customUsageTags);
  return merged;
}

// Função para aplicar overlay (intensidade + cor) no CSS
const applyOverlay = (intensity: OverlayIntensity, color: OverlayColor) => {
  const opacity = OVERLAY_INTENSITY_VALUES[intensity];
  const rgb = OVERLAY_COLOR_VALUES[color];
  document.documentElement.style.setProperty('--overlay-bg', `rgba(${rgb}, ${opacity})`);
  document.documentElement.style.setProperty('--floating-overlay-color', rgb);
};

// Função para aplicar escala de fonte no CSS
const applyFontScale = (scale: FontScale, weight: FontWeight) => {
  document.documentElement.style.setProperty(
    '--bigwa-content-scale',
    String(FONT_SCALE_VALUES[scale])
  );
  document.documentElement.style.setProperty(
    '--bigwa-content-weight',
    String(FONT_WEIGHT_VALUES[weight])
  );
};

// ===== DRIVE SYNC (cross-device) =====
let _lastMutationAt = 0;
let _prefsLoadedAt: number | null = null;
const PREFS_TTL = 300_000; // 5 min

let _prefsSaveTimer: ReturnType<typeof setTimeout> | null = null;
let _prefsSaveMaxTimer: ReturnType<typeof setTimeout> | null = null;
const PREFS_SAVE_DEBOUNCE = 3_000;
const PREFS_SAVE_MAX_WAIT = 30_000;

function _schedulePrefsSave() {
  const prefs = usePreferencesStore.getState().preferences;
  if (_prefsSaveTimer) clearTimeout(_prefsSaveTimer);
  if (!_prefsSaveMaxTimer) {
    _prefsSaveMaxTimer = setTimeout(() => {
      _prefsSaveMaxTimer = null;
      if (_prefsSaveTimer) clearTimeout(_prefsSaveTimer);
      _prefsSaveTimer = null;
      googleDrive.saveUserPreferences(prefs as unknown as Record<string, unknown>).catch(err => {
        logger.error('[preferencesStore] Flush save falhou:', err);
      });
    }, PREFS_SAVE_MAX_WAIT);
  }
  _prefsSaveTimer = setTimeout(() => {
    _prefsSaveTimer = null;
    if (_prefsSaveMaxTimer) { clearTimeout(_prefsSaveMaxTimer); _prefsSaveMaxTimer = null; }
    googleDrive.saveUserPreferences(prefs as unknown as Record<string, unknown>).catch(err => {
      logger.error('[preferencesStore] Debounced save falhou:', err);
    });
  }, PREFS_SAVE_DEBOUNCE);
}

export function flushPreferencesQueue() {
  if (_prefsSaveTimer || _prefsSaveMaxTimer) {
    if (_prefsSaveTimer) { clearTimeout(_prefsSaveTimer); _prefsSaveTimer = null; }
    if (_prefsSaveMaxTimer) { clearTimeout(_prefsSaveMaxTimer); _prefsSaveMaxTimer = null; }
    const prefs = usePreferencesStore.getState().preferences;
    // Sync flush — best-effort, won't block unload
    googleDrive.saveUserPreferences(prefs as unknown as Record<string, unknown>).catch(() => {});
  }
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      preferences: { ...DEFAULT_PREFERENCES },
      isLoading: false,
      
      applyTemplate: (template) => {
        const presets = {
          pesquisa: { showEmpiricalResearch: true, showConexoes: true, showCitacoes: true, showColaboracao: true },
          leitura: { showEmpiricalResearch: false, showConexoes: false, showCitacoes: false, showColaboracao: false },
          personalizado: {},
        };
        const preset = presets[template];
        Object.entries(preset).forEach(([key, value]) => {
          get().updatePreference(key as keyof BigwaPreferences, value);
        });
      },

      updatePreference: (key, value) => {
        // Update otimista - atualiza UI imediatamente
        set(state => ({
          preferences: {
            ...state.preferences,
            [key]: value,
          },
        }));
        _lastMutationAt = Date.now();
        _schedulePrefsSave();

        // Aplicar escala de fonte se necessário
        if (key === 'fontScale' || key === 'fontWeight') {
          const prefs = get().preferences;
          applyFontScale(
            key === 'fontScale' ? value as FontScale : prefs.fontScale,
            key === 'fontWeight' ? value as FontWeight : prefs.fontWeight
          );
        }

        // Aplicar overlay se necessário
        if (key === 'overlayIntensity' || key === 'overlayColor') {
          const prefs = get().preferences;
          applyOverlay(prefs.overlayIntensity, prefs.overlayColor);
        }

        // Notificar outras abas via BroadcastChannel
        try {
          const channel = new BroadcastChannel('bigwa-preferences');
          channel.postMessage({
            type: 'preference-update',
            key,
            value,
            timestamp: Date.now(),
          });
          channel.close();
        } catch (_e) {
          // BroadcastChannel não suportado
        }
      },
      
      loadPreferences: async () => {
        set({ isLoading: true });
        
        // Aplicar escala de fonte ao carregar
        const prefs = get().preferences;
        applyFontScale(prefs.fontScale, prefs.fontWeight);
        applyOverlay(prefs.overlayIntensity, prefs.overlayColor);

        set({ isLoading: false });
      },
      
      resetPreferences: () => {
        set({ preferences: { ...DEFAULT_PREFERENCES } });
        applyFontScale(DEFAULT_PREFERENCES.fontScale, DEFAULT_PREFERENCES.fontWeight);
        applyOverlay(DEFAULT_PREFERENCES.overlayIntensity, DEFAULT_PREFERENCES.overlayColor);
      },
      
      // ===== HANDLERS PARA NOMEAÇÃO DE PDFs =====
      
      updateNamingPreference: (key, value) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            namingPreferences: {
              ...state.preferences.namingPreferences,
              [key]: value,
            },
          },
        }));
      },
      
      resetNamingPreferences: () => {
        set(state => ({
          preferences: {
            ...state.preferences,
            namingPreferences: { ...DEFAULT_NAMING_PREFERENCES },
          },
        }));
      },
      
      // ===== HANDLERS PARA TAGS DE CONTEÚDO =====
      
      addCustomContentTag: (tag: string) => {
        const trimmed = tag.trim().toLowerCase().slice(0, 100);
        if (!trimmed) return;
        
        const current = get().preferences.customContentTags || [];
        if (current.includes(trimmed)) return;
        
        set(state => ({
          preferences: {
            ...state.preferences,
            customContentTags: [...current, trimmed],
          },
        }));
      },
      
      removeCustomContentTag: (tag: string) => {
        const current = get().preferences.customContentTags || [];
        set(state => ({
          preferences: {
            ...state.preferences,
            customContentTags: current.filter(t => t !== tag),
          },
        }));
      },
      
      resetCustomContentTags: () => {
        set(state => ({
          preferences: {
            ...state.preferences,
            customContentTags: [],
          },
        }));
      },
      
      // ===== HANDLERS PARA TAGS DE USO (DEPRECATED v3.0) =====
      // Mantidos para retrocompatibilidade com dados existentes
      
      addCustomUsageTag: (tag: string) => {
        const trimmed = tag.trim().toLowerCase().slice(0, 100);
        if (!trimmed) return;
        
        const current = get().preferences.customUsageTags || [];
        if (current.includes(trimmed)) return;
        
        set(state => ({
          preferences: {
            ...state.preferences,
            customUsageTags: [...current, trimmed],
          },
        }));
      },
      
      removeCustomUsageTag: (tag: string) => {
        const current = get().preferences.customUsageTags || [];
        set(state => ({
          preferences: {
            ...state.preferences,
            customUsageTags: current.filter(t => t !== tag),
          },
        }));
      },
      
      resetCustomUsageTags: () => {
        set(state => ({
          preferences: {
            ...state.preferences,
            customUsageTags: [],
          },
        }));
      },

      // ===== HANDLERS PARA MINHAS OBRAS =====

      addAlternativeName: (name: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;

        const current = get().preferences.alternativeNames || [];
        if (current.includes(trimmed)) return;

        set(state => ({
          preferences: {
            ...state.preferences,
            alternativeNames: [...current, trimmed],
          },
        }));
      },

      removeAlternativeName: (name: string) => {
        const current = get().preferences.alternativeNames || [];
        set(state => ({
          preferences: {
            ...state.preferences,
            alternativeNames: current.filter(n => n !== name),
          },
        }));
      },

      // ===== HANDLERS PARA STATUS DE LEITURA =====

      addReadingStatus: (name: string, color: string) => {
        const trimmed = name.trim();
        if (!trimmed) return;

        const current = get().preferences.readingStatuses || [...DEFAULT_READING_STATUSES];
        if (current.length >= MAX_READING_STATUSES) return;

        const newStatus: ReadingStatus = {
          id: crypto.randomUUID(),
          name: trimmed,
          color,
          order: current.length,
          isDefault: false,
        };

        set(state => ({
          preferences: {
            ...state.preferences,
            readingStatuses: [...(state.preferences.readingStatuses || [...DEFAULT_READING_STATUSES]), newStatus],
          },
        }));
        _lastMutationAt = Date.now();
        _schedulePrefsSave();
      },

      updateReadingStatus: (id: string, updates: Partial<Pick<ReadingStatus, 'name' | 'color'>>) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            readingStatuses: (state.preferences.readingStatuses || [...DEFAULT_READING_STATUSES]).map(s =>
              s.id === id ? { ...s, ...updates } : s
            ),
          },
        }));
        _lastMutationAt = Date.now();
        _schedulePrefsSave();
      },

      removeReadingStatus: (id: string) => {
        const current = get().preferences.readingStatuses || [...DEFAULT_READING_STATUSES];
        const target = current.find(s => s.id === id);
        if (!target || target.isDefault) return;

        const updated = current
          .filter(s => s.id !== id)
          .map((s, i) => ({ ...s, order: i }));

        set(state => ({
          preferences: {
            ...state.preferences,
            readingStatuses: updated,
          },
        }));
        _lastMutationAt = Date.now();
        _schedulePrefsSave();
      },

      // ===== HANDLERS PARA TAGS EMPÍRICAS =====

      setActiveTechniques: (techniqueIds: string[]) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            activeTechniques: techniqueIds,
          },
        }));
        _lastMutationAt = Date.now();
        _schedulePrefsSave();
      },

      addCustomEmpiricalTag: (tag: EmpiricalTagDefinition) => {
        const current = get().preferences.customEmpiricalTags || [];
        if (current.some(t => t.id === tag.id)) return;

        set(state => ({
          preferences: {
            ...state.preferences,
            customEmpiricalTags: [...current, tag],
          },
        }));
        _lastMutationAt = Date.now();
        _schedulePrefsSave();
      },

      removeCustomEmpiricalTag: (tagId: string) => {
        const current = get().preferences.customEmpiricalTags || [];
        set(state => ({
          preferences: {
            ...state.preferences,
            customEmpiricalTags: current.filter(t => t.id !== tagId),
          },
        }));
        _lastMutationAt = Date.now();
        _schedulePrefsSave();
      },

      setEmpiricalOnboardingCompleted: (completed: boolean) => {
        set(state => ({
          preferences: {
            ...state.preferences,
            empiricalOnboardingCompleted: completed,
          },
        }));
        _lastMutationAt = Date.now();
        _schedulePrefsSave();
      },

      // ===== DRIVE SYNC =====

      mergeFromDrive: (drivePrefs: Partial<BigwaPreferences>) => {
        const startedAt = _prefsLoadedAt ?? 0;
        if (_lastMutationAt > startedAt) {
          logger.info('[preferencesStore]', 'mergeFromDrive descartado — mutação local recente');
          return;
        }
        set({ preferences: mergeExternalPreferences(drivePrefs) });
        _prefsLoadedAt = Date.now();
        // Re-apply CSS effects
        const prefs = get().preferences;
        applyFontScale(prefs.fontScale, prefs.fontWeight);
        applyOverlay(prefs.overlayIntensity, prefs.overlayColor);
      },

      revalidatePreferencesIfStale: () => {
        if (_prefsLoadedAt && Date.now() - _prefsLoadedAt < PREFS_TTL) return;
        if (_lastMutationAt > (_prefsLoadedAt ?? 0)) return;
        const startedAt = Date.now();
        googleDrive.loadUserPreferences().then(drivePrefs => {
          if (!drivePrefs) return;
          if (_lastMutationAt > startedAt) {
            logger.info('[preferencesStore]', 'Revalidação descartada — mutação local mais recente');
            return;
          }
          set({ preferences: mergeExternalPreferences(drivePrefs as Partial<BigwaPreferences>) });
          _prefsLoadedAt = Date.now();
          const prefs = get().preferences;
          applyFontScale(prefs.fontScale, prefs.fontWeight);
          applyOverlay(prefs.overlayIntensity, prefs.overlayColor);
        }).catch(err => {
          logger.error('[preferencesStore] Revalidação de preferences falhou:', err);
        });
      },
    }),
    {
      name: 'bigwa-preferences',
      partialize: (state) => ({ preferences: state.preferences }),
      merge: (persisted, current) => {
        const persistedState = persisted as { preferences?: Partial<BigwaPreferences> } | undefined;
        return {
          ...current,
          preferences: mergeExternalPreferences(persistedState?.preferences),
        };
      },
      onRehydrateStorage: () => (state) => {
        // Aplicar escala de fonte e overlay ao rehidratar do localStorage
        if (state?.preferences) {
          applyFontScale(state.preferences.fontScale, state.preferences.fontWeight);
          applyOverlay(state.preferences.overlayIntensity, state.preferences.overlayColor);
        }
      },
    }
  )
);

// Listener para sincronizar entre abas
if (typeof window !== 'undefined') {
  try {
    const channel = new BroadcastChannel('bigwa-preferences');
    channel.onmessage = (event) => {
      if (event.data.type === 'preference-update') {
        const { key, value } = event.data;
        // Aba vizinha pode estar numa versão antiga do app: tags custom passam
        // pela mesma normalização das demais fronteiras de entrada.
        const safeValue = key === 'customContentTags' || key === 'customUsageTags'
          ? normalizeCustomTagList(value)
          : value;
        usePreferencesStore.setState(state => ({
          preferences: {
            ...state.preferences,
            [key]: safeValue,
          },
        }));
        
        // Aplicar escala de fonte se necessário
        if (key === 'fontScale' || key === 'fontWeight') {
          const prefs = usePreferencesStore.getState().preferences;
          applyFontScale(prefs.fontScale, prefs.fontWeight);
        }

        // Aplicar overlay se necessário
        if (key === 'overlayIntensity' || key === 'overlayColor') {
          const prefs = usePreferencesStore.getState().preferences;
          applyOverlay(prefs.overlayIntensity, prefs.overlayColor);
        }
      }
    };
  } catch (_e) {
    // BroadcastChannel não suportado
  }
}
